import { useState, useEffect, useRef, Fragment } from 'react'
import { entitiesApi, NerResults, NerEntity, ExtractionResponse } from '../api/entities'
import { aiApi } from '../api/ai'
import { datasetApi } from '../api/datasets'
import { useSession } from '../contexts/SessionContext'
import BookmarkStar from '../components/BookmarkStar'
import ExtractionJobsPanel from '../components/ExtractionJobsPanel'

// All entity labels produced by en_ner_bionlp13cg_md. GENE_OR_GENE_PRODUCT is the
// gene/protein output; the rest are offered for users who want broader extraction.
const ALL_TYPES = [
  'GENE_OR_GENE_PRODUCT',
  'SIMPLE_CHEMICAL',
  'AMINO_ACID',
  'CELL',
  'CELLULAR_COMPONENT',
  'CANCER',
  'ORGANISM',
  'TISSUE',
  'ORGAN',
  'MULTI_TISSUE_STRUCTURE',
  'ORGANISM_SUBDIVISION',
  'ORGANISM_SUBSTANCE',
  'PATHOLOGICAL_FORMATION',
  'DEVELOPING_ANATOMICAL_STRUCTURE',
  'ANATOMICAL_SYSTEM',
  'IMMATERIAL_ANATOMICAL_ENTITY',
]
const DEFAULT_TYPES = ['GENE_OR_GENE_PRODUCT']
const LS_KEY = 'pmc_ev_last_ner_id'

function ncbiHref(pmcid: string): string {
  return pmcid.startsWith('PMID_')
    ? `https://pubmed.ncbi.nlm.nih.gov/${pmcid.replace('PMID_', '')}/`
    : `https://www.ncbi.nlm.nih.gov/pmc/articles/${pmcid}`
}

export default function GenesProteinsPage() {
  const { activeSessionId } = useSession()
  const [datasetId, setDatasetId] = useState('')
  const [datasets, setDatasets] = useState<{ dataset_id: string; name: string; row_count: number }[]>([])
  const [selectedTypes, setSelectedTypes] = useState<string[]>([...DEFAULT_TYPES])
  const [showAllTypes, setShowAllTypes] = useState(false)
  const [maxRows, setMaxRows] = useState(400)
  const [maxRowsCap, setMaxRowsCap] = useState(1000)  // admin-configured ceiling
  const [rowSelection, setRowSelection] = useState<'top' | 'random'>('top')

  const [isRunning, setIsRunning] = useState(false)
  const [result, setResult] = useState<NerResults | null>(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [currentId, setCurrentId] = useState<string | null>(null)
  const [loadedName, setLoadedName] = useState('')

  // Results view controls
  const [typeFilter, setTypeFilter] = useState<string>('')   // '' = all kept types
  const [expanded, setExpanded] = useState<string | null>(null)
  const [sortKey, setSortKey] = useState<'entity' | 'type' | 'count' | 'pmcid_count'>('count')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  // Bump to make the Extraction Jobs panel refetch (after start/cancel/complete).
  const [jobsRefresh, setJobsRefresh] = useState(0)
  const refreshJobs = () => setJobsRefresh(n => n + 1)

  const pollInterval = useRef<ReturnType<typeof setInterval> | null>(null)

  // Load the admin-configured max-rows ceiling once and clamp the slider to it.
  useEffect(() => {
    aiApi.getConfig()
      .then(r => {
        const cap = r.data.max_rows || 1000
        setMaxRowsCap(cap)
        setMaxRows(prev => Math.min(prev, cap))
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!activeSessionId) {
      setDatasets([]); setDatasetId(''); setCurrentId(null); setResult(null); setLoadedName('')
      return
    }
    datasetApi.list(activeSessionId).then(r => {
      setDatasets(r.data)
      setDatasetId(prev => (r.data.some(d => d.dataset_id === prev) ? prev : ''))
    }).catch(() => setDatasets([]))

    const savedId = localStorage.getItem(LS_KEY)
    if (savedId) loadById(savedId)
    else { setCurrentId(null); setResult(null); setLoadedName('') }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionId])

  useEffect(() => () => { if (pollInterval.current) clearInterval(pollInterval.current) }, [])

  // Default the slider to the whole dataset whenever the selected dataset changes.
  useEffect(() => {
    const d = datasets.find(x => x.dataset_id === datasetId)
    if (d) setMaxRows(d.row_count || 1)
  }, [datasetId, datasets])

  const loadById = async (id: string) => {
    try {
      const res = await entitiesApi.getExtraction(id)
      if (activeSessionId && res.data.session_id && res.data.session_id !== activeSessionId) {
        localStorage.removeItem(LS_KEY); setCurrentId(null); setResult(null); setLoadedName(''); return
      }
      const p = res.data.params || {}
      if (Array.isArray(p.entity_types) && p.entity_types.length) {
        setSelectedTypes(p.entity_types)
        if (p.entity_types.some((t: string) => !DEFAULT_TYPES.includes(t))) setShowAllTypes(true)
      }
      if (typeof p.max_rows === 'number') setMaxRows(p.max_rows)
      if (p.row_selection === 'random' || p.row_selection === 'top') setRowSelection(p.row_selection)
      if (res.data.status === 'complete') {
        setCurrentId(id); setResult(res.data.results || null); setLoadedName(res.data.name)
      } else if (res.data.status === 'running' || res.data.status === 'pending') {
        setCurrentId(id); setLoadedName(res.data.name); setIsRunning(true); startPolling(id)
      }
    } catch {
      localStorage.removeItem(LS_KEY)
    }
  }

  // Load a job already fetched by the Extraction Jobs panel.
  const handleLoadJob = (job: ExtractionResponse) => {
    const p = job.params || {}
    if (Array.isArray(p.entity_types) && p.entity_types.length) {
      setSelectedTypes(p.entity_types)
      if (p.entity_types.some((t: string) => !DEFAULT_TYPES.includes(t))) setShowAllTypes(true)
    }
    if (typeof p.max_rows === 'number') setMaxRows(p.max_rows)
    if (p.row_selection === 'random' || p.row_selection === 'top') setRowSelection(p.row_selection)
    setErrorMsg(''); setExpanded(null); setTypeFilter('')
    setCurrentId(job.analysis_id)
    setResult(job.results || null)
    setLoadedName(job.name)
    localStorage.setItem(LS_KEY, job.analysis_id)
  }

  const toggleType = (t: string) => {
    setSelectedTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])
  }

  const startExtraction = async () => {
    setErrorMsg(''); setResult(null); setLoadedName(''); setExpanded(null); setTypeFilter('')
    if (!datasetId) { setErrorMsg('Select a dataset. Save your search results as a dataset first.'); return }
    if (selectedTypes.length === 0) { setErrorMsg('Select at least one entity type.'); return }
    setIsRunning(true)
    try {
      const res = await entitiesApi.extract({
        dataset_id: datasetId,
        entity_types: selectedTypes,
        max_rows: maxRows,
        row_selection: rowSelection,
      })
      setCurrentId(res.data.analysis_id)
      localStorage.setItem(LS_KEY, res.data.analysis_id)
      startPolling(res.data.analysis_id)
      refreshJobs()
    } catch (e: any) {
      setErrorMsg(e.response?.data?.detail || 'Extraction failed to start')
      setIsRunning(false)
    }
  }

  const startPolling = (id: string) => {
    if (pollInterval.current) clearInterval(pollInterval.current)
    pollInterval.current = setInterval(async () => {
      try {
        const res = await entitiesApi.getExtraction(id)
        const s = res.data.status
        if (s === 'complete') {
          clearInterval(pollInterval.current!); setIsRunning(false)
          setResult(res.data.results || null); setLoadedName(res.data.name); refreshJobs()
        } else if (s === 'error') {
          clearInterval(pollInterval.current!); setIsRunning(false)
          setErrorMsg(res.data.error_message || 'Extraction failed'); refreshJobs()
        } else if (s === 'cancelled') {
          clearInterval(pollInterval.current!); setIsRunning(false)
          setErrorMsg('Extraction was cancelled'); refreshJobs()
        }
      } catch {
        clearInterval(pollInterval.current!); setIsRunning(false)
      }
    }, 3000)
  }

  const cancelRun = async () => {
    if (!currentId) return
    try { await entitiesApi.cancel(currentId); refreshJobs() } catch { /* ignore */ }
  }

  const exportCsv = () => {
    if (!result) return
    const rows = sortedEntities
    const header = ['entity', 'type', 'count', 'article_count', 'pmcids']
    const esc = (v: any) => {
      const s = String(v ?? '')
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const lines = [header.join(',')]
    for (const e of rows) {
      lines.push([e.entity, e.type, e.count, e.pmcid_count, e.pmcids.join(';')].map(esc).join(','))
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `entities_${loadedName || 'extraction'}_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const selectedDataset = datasets.find(d => d.dataset_id === datasetId)
  // Slider spans the selected dataset (no artificial cap — NER is local CPU work).
  const sliderMax = selectedDataset?.row_count || maxRowsCap
  const sliderMin = Math.min(50, sliderMax)
  // step=1 so the exact dataset size is always reachable (a coarse step leaves the
  // true max off the grid — e.g. a 2,944-row dataset would top out at 2,850).
  const sliderStep = 1
  const canStart = !!datasetId && selectedTypes.length > 0 && !isRunning

  const allKeptTypes = result ? Object.keys(
    result.entities.reduce((acc, e) => { acc[e.type] = true; return acc }, {} as Record<string, boolean>)
  ) : []
  const filteredEntities: NerEntity[] = result
    ? result.entities.filter(e => !typeFilter || e.type === typeFilter)
    : []
  const sortedEntities: NerEntity[] = [...filteredEntities].sort((a, b) => {
    const av = a[sortKey] as string | number
    const bv = b[sortKey] as string | number
    const cmp = typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true })
    return sortDir === 'asc' ? cmp : -cmp
  })
  const toggleSort = (k: typeof sortKey) => {
    if (sortKey === k) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(k); setSortDir(k === 'entity' || k === 'type' ? 'asc' : 'desc') }
  }
  const sortInd = (k: typeof sortKey) => (sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '')

  return (
    <div>
      {!activeSessionId && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
          No session selected. Datasets and extractions are scoped to a session — pick or create one
          from the <strong>Session</strong> picker in the top bar.
        </div>
      )}
      <div className="flex gap-6">
        {/* Sidebar */}
        <div className="w-80 shrink-0 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Dataset</h3>
            <p className="text-xs text-gray-500 mb-2">Extraction runs on a saved dataset's sentences.</p>
            <select
              value={datasetId}
              onChange={e => setDatasetId(e.target.value)}
              className="w-full px-2 py-1 border rounded text-sm"
            >
              <option value="">Select a saved dataset...</option>
              {datasets.map(d => (
                <option key={d.dataset_id} value={d.dataset_id}>{d.name} ({d.row_count} rows)</option>
              ))}
            </select>
            {selectedDataset && (
              <div className="text-xs text-gray-500 bg-gray-50 rounded px-2 py-1 mt-2">
                <strong>{selectedDataset.row_count?.toLocaleString()}</strong> rows
              </div>
            )}
            {datasets.length === 0 && (
              <div className="text-xs text-yellow-600 bg-yellow-50 rounded px-2 py-1 mt-2">
                {activeSessionId
                  ? 'No datasets in this session. Run a search, then save results as a dataset.'
                  : 'Select a session (top bar) to see its datasets.'}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Settings</h3>

            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">
                Max Rows ({maxRows.toLocaleString()})
                <span className="text-gray-400"> · of {sliderMax.toLocaleString()}</span>
              </label>
              <input
                type="range"
                min={sliderMin}
                max={sliderMax}
                step={sliderStep}
                value={Math.min(maxRows, sliderMax)}
                disabled={!selectedDataset}
                onChange={e => setMaxRows(Number(e.target.value))}
                className="w-full"
              />
              {!selectedDataset && <p className="text-[11px] text-gray-400 mt-1">Select a dataset to set the range.</p>}
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Row Selection</label>
              <select
                value={rowSelection}
                onChange={e => setRowSelection(e.target.value as any)}
                className="w-full px-2 py-1 border rounded text-sm"
              >
                <option value="top">Top</option>
                <option value="random">Random</option>
              </select>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Entity types</h3>
            <p className="text-xs text-gray-500 mb-3">
              Genes &amp; proteins (<code>GENE_OR_GENE_PRODUCT</code>) by default. Model: SciSpaCy
              <code className="ml-1">en_ner_bionlp13cg_md</code>.
            </p>
            <div className="space-y-1.5">
              {(showAllTypes ? ALL_TYPES : DEFAULT_TYPES).map(t => (
                <label key={t} className="flex items-center gap-2 text-xs text-gray-700">
                  <input type="checkbox" checked={selectedTypes.includes(t)} onChange={() => toggleType(t)} />
                  <span className="font-mono">{t}</span>
                </label>
              ))}
            </div>
            <button
              onClick={() => setShowAllTypes(v => !v)}
              className="mt-2 text-xs text-blue-600 hover:underline"
            >
              {showAllTypes ? 'Show fewer' : 'Show all 16 types'}
            </button>
          </div>

          <ExtractionJobsPanel
            onLoadJob={handleLoadJob}
            activeAnalysisId={currentId}
            activeSessionId={activeSessionId}
            refreshSignal={jobsRefresh}
          />
        </div>

        {/* Main */}
        <div className="flex-1 min-w-0 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-2">Genes &amp; Proteins</h2>
            <p className="text-sm text-gray-500 mb-4">
              Extract gene/protein (and optionally other biomedical) entity names from the sentences in a
              saved dataset using SciSpaCy biomedical NER.
            </p>
            <div className="flex gap-2">
              <button
                onClick={startExtraction}
                disabled={!canStart}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {isRunning ? 'Extracting...' : 'Run extraction'}
              </button>
              {isRunning && (
                <button onClick={cancelRun} className="px-4 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
              )}
            </div>
            {errorMsg && <div className="mt-4 text-red-600 text-sm">{errorMsg}</div>}
          </div>

          {isRunning && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                <span className="text-gray-600">Running NER over the dataset… This may take a few minutes for large datasets.</span>
              </div>
            </div>
          )}

          {result && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="text-lg font-semibold">
                  Entities
                  {loadedName && <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{loadedName}</span>}
                </h2>
                <div className="flex items-center gap-2">
                  {allKeptTypes.length > 1 && (
                    <select
                      value={typeFilter}
                      onChange={e => setTypeFilter(e.target.value)}
                      className="px-2 py-1 border rounded text-sm"
                    >
                      <option value="">All types</option>
                      {allKeptTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  )}
                  <button
                    onClick={exportCsv}
                    disabled={filteredEntities.length === 0}
                    className="px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                  >
                    Export CSV
                  </button>
                </div>
              </div>

              <div className="text-xs text-gray-500 mb-3">
                {filteredEntities.length.toLocaleString()} unique entities ·
                scanned {result.sentences_scanned.toLocaleString()} sentences of {result.rows_total.toLocaleString()} rows
                {Object.keys(result.type_counts).length > 0 && (
                  <span className="ml-2">
                    · mentions: {Object.entries(result.type_counts).slice(0, 6).map(([t, c]) => `${t} ${c}`).join(', ')}
                  </span>
                )}
              </div>

              {filteredEntities.length === 0 ? (
                <p className="text-sm text-gray-500">No entities found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">
                          <button onClick={() => toggleSort('entity')} className="hover:text-gray-900">Entity{sortInd('entity')}</button>
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">
                          <button onClick={() => toggleSort('type')} className="hover:text-gray-900">Type{sortInd('type')}</button>
                        </th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700">
                          <button onClick={() => toggleSort('count')} className="hover:text-gray-900">Mentions{sortInd('count')}</button>
                        </th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700">
                          <button onClick={() => toggleSort('pmcid_count')} className="hover:text-gray-900">Articles{sortInd('pmcid_count')}</button>
                        </th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {sortedEntities.map((e, idx) => {
                        const key = `${e.type}:${e.entity}:${idx}`
                        const isOpen = expanded === key
                        return (
                          <Fragment key={key}>
                            <tr className="hover:bg-gray-50 align-top">
                              <td className="px-3 py-2 font-medium">{e.entity}</td>
                              <td className="px-3 py-2 text-gray-500 font-mono text-xs">{e.type}</td>
                              <td className="px-3 py-2 text-right">{e.count}</td>
                              <td className="px-3 py-2 text-right">{e.pmcid_count}</td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  onClick={() => setExpanded(isOpen ? null : key)}
                                  className="text-xs text-blue-600 hover:underline"
                                >
                                  {isOpen ? 'Hide' : 'Examples'}
                                </button>
                              </td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-gray-50">
                                <td colSpan={5} className="px-3 py-2">
                                  <ul className="space-y-1">
                                    {e.examples.map((ex, i) => (
                                      <li key={i} className="text-xs text-gray-600">
                                        {ex.pmcid && (
                                          <span className="inline-flex items-center gap-1 mr-1 whitespace-nowrap">
                                            <BookmarkStar pmcid={ex.pmcid} />
                                            <a href={ncbiHref(ex.pmcid)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-mono">
                                              {ex.pmcid.startsWith('PMID_') ? `PMID:${ex.pmcid.replace('PMID_', '')}` : ex.pmcid}
                                            </a>
                                          </span>
                                        )}
                                        {ex.sentence}
                                      </li>
                                    ))}
                                  </ul>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
